// Bridges the chat-pane debugging tools to the live LiveBrowser iframe. The
// mounted LiveBrowser registers its agent-control port here keyed by step name;
// the tool navigates the UI to the target step, waits for that step to register
// with a renderable execution, then issues a command over the live port.

import type {
  AgentAction,
  AgentCommandResult,
} from "../components/LiveBrowser/useAgentControl";

type LiveAgentControl = {
  whenReady: () => Promise<void>;
  inspect: (selector?: string) => Promise<AgentCommandResult>;
  getConsole: () => Promise<AgentCommandResult>;
  interact: (
    actions: AgentAction[],
    selector?: string
  ) => Promise<AgentCommandResult>;
  screenshot: () => Promise<AgentCommandResult>;
};

// What the mounted LiveBrowser knows about the step it's showing: whether
// the step serves a page on this branch at all.
type StepPreviewStatus = "none" | "renderable";

type Registration = {
  stepName: string;
  status: StepPreviewStatus;
  control: LiveAgentControl;
};

let current: Registration | null = null;
const waiters = new Set<() => void>();

export function registerAgentControl(reg: Registration): () => void {
  current = reg;
  for (const w of waiters) w();
  return () => {
    if (current?.control === reg.control) current = null;
  };
}

// Resolves once a LiveBrowser for `step` has registered, or rejects after
// the deadline. No polling: registration notifies waiters directly.
function whenStepSettled(
  step: string,
  timeoutMs: number
): Promise<StepPreviewStatus> {
  const settled = (r: Registration | null) => r?.stepName === step;
  if (settled(current)) return Promise.resolve(current!.status);
  return new Promise<StepPreviewStatus>((resolve, reject) => {
    const timer = setTimeout(() => {
      waiters.delete(check);
      reject(
        new Error(
          `Timed out waiting for the “${step}” preview to mount. It may not be a webpage step, or the browser view is unavailable.`
        )
      );
    }, timeoutMs);
    const check = () => {
      if (!settled(current)) return;
      clearTimeout(timer);
      waiters.delete(check);
      resolve(current!.status);
    };
    waiters.add(check);
  });
}

export type DriveCommand =
  | { kind: "inspect"; selector?: string }
  | { kind: "console" }
  | { kind: "interact"; actions: AgentAction[]; selector?: string }
  | { kind: "screenshot" };

const MOUNT_TIMEOUT_MS = 15_000;

export async function driveLiveStep(
  stepName: string,
  command: DriveCommand,
  navigateToStep: (step: string) => void
): Promise<AgentCommandResult> {
  navigateToStep(stepName);
  const status = await whenStepSettled(stepName, MOUNT_TIMEOUT_MS);

  if (status === "none") {
    return {
      error: `Step “${stepName}” doesn’t serve a webpage on this branch, so there’s no page to inspect.`,
    };
  }

  const control = current?.control;
  if (!control) throw new Error(`No live preview for “${stepName}”`);
  await control.whenReady();
  if (command.kind === "inspect") return control.inspect(command.selector);
  if (command.kind === "console") return control.getConsole();
  if (command.kind === "screenshot") return control.screenshot();
  return control.interact(command.actions, command.selector);
}
